import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip, FormHelperText, InputAdornment, InputLabel, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { Controller, FieldValues } from 'react-hook-form';

import { BaseInputProps } from './GetFormInput';

// Basic email validation regex
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export function FormAddEmail<T extends FieldValues>({
    control,
    disabled,
    fullWidth,
    helperText,
    label,
    required,
    small,
    validation,
    value,
}: Readonly<BaseInputProps<T>>) {
    // Don't use dependentField for now to avoid TypeScript errors with useWatch
    const isDisabled = disabled;

    // Local state for current email input
    const [currentEmail, setCurrentEmail] = useState('');
    const [inputError, setInputError] = useState<string | null>(null);

    const validateEmail = (email: string): boolean => {
        if (!email) {
            setInputError('Email cannot be empty');
            return false;
        }

        if (!emailRegex.test(email)) {
            setInputError('Please enter a valid email address');
            return false;
        }

        setInputError(null);
        return true;
    };

    return (
        <Controller
            control={control}
            disabled={isDisabled}
            name={value}
            render={({ field: { onChange, ref, value: emails = [] }, fieldState: { error } }) => {
                // Ensure emails is always an array
                const emailsArray: string[] = Array.isArray(emails) ? emails : [];

                const handleAddEmail = () => {
                    if (!validateEmail(currentEmail)) {
                        return;
                    }

                    // Check for duplicates
                    if (emailsArray.includes(currentEmail)) {
                        setInputError('This email is already added');
                        return;
                    }

                    // Add email to the array
                    const updatedEmails = [...emailsArray, currentEmail];
                    onChange(updatedEmails);
                    setCurrentEmail('');
                };

                const handleRemoveEmail = (emailToRemove: string) => {
                    const updatedEmails = emailsArray.filter((email) => email !== emailToRemove);
                    onChange(updatedEmails);
                };

                const handleKeyPress = (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEmail();
                    }
                };

                return (
                    <Box>
                        <InputLabel sx={{ color: 'black', fontWeight: 'medium', mb: 0.5 }}>{label}</InputLabel>
                        <TextField
                            disabled={disabled || isDisabled}
                            error={!!inputError || !!error}
                            fullWidth
                            helperText={inputError || (error?.message ?? helperText)}
                            onChange={(e) => setCurrentEmail(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Enter email address"
                            ref={ref}
                            size={small ? 'small' : 'medium'}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Button
                                                disabled={disabled || isDisabled || !currentEmail}
                                                onClick={handleAddEmail}
                                                size="small"
                                                startIcon={<AddIcon />}
                                                variant="contained"
                                            >
                                                Add
                                            </Button>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ maxWidth: fullWidth ? undefined : 350 }}
                            value={currentEmail}
                            variant="outlined"
                        />
                        {emailsArray.length > 0 && (
                            <Stack
                                direction="row"
                                sx={{
                                    flexWrap: 'wrap',
                                    py: 2,
                                }}
                            >
                                {emailsArray.map((email) => (
                                    <Chip
                                        color="primary"
                                        key={email}
                                        label={email}
                                        onDelete={() => handleRemoveEmail(email)}
                                        size="small"
                                        sx={{ maxWidth: '100%', mb: 0.5, mr: 0.5 }}
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        )}
                        {emailsArray.length === 0 && required ? (
                            <FormHelperText error>
                                {`${label || 'Email'} is required. Please add at least one email.`}
                            </FormHelperText>
                        ) : null}
                    </Box>
                );
            }}
            rules={{
                required: required ? `${label ?? value} cannot be empty` : false,
                validate: (value) => {
                    // For array validation
                    if (required && (!value || !Array.isArray(value) || value.length === 0)) {
                        return `${label ?? 'Email'} is required`;
                    }

                    // If custom validation is provided, use it
                    if (value && validation) {
                        const result = validation.safeParse(value);
                        return result.success ? true : result.error.errors[0]?.message;
                    }

                    return true;
                },
            }}
        />
    );
}
